import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, CheckCircle, Camera, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { UPDATE_LOST_ANIMAL } from '../../services/apollo/mutations/lostAnimalMutations';
import { UPDATE_FOUND_ANIMAL } from '../../services/apollo/mutations/foundAnimalMutations';
import { UPDATE_PSA } from '../../services/apollo/mutations/psaMutations';
import { UPDATE_PET_SERVICE } from '../../services/apollo/mutations/petServiceMutations';
import { FEED_TYPES } from '../../hooks/notifications/useCombinedFeed';
import { triggerFeedRefresh } from '../../slices/notificationsSlice';
import useCloudFlareImages from '../../hooks/useCloudFlareImages';
import ImageCropper from '../ImageCropper/ImageCropper';

const EditPostModal = ({ 
  isOpen, 
  onClose,
  postData,
  postType,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  
  const animalTypeOptions = useMemo(() => [
    { name: 'dog', value: t('dog', 'Dog'), label: t('dog', 'Dog') },
    { name: 'cat', value: t('cat', 'Cat'), label: t('cat', 'Cat') },
    { name: 'bird', value: t('bird', 'Bird'), label: t('bird', 'Bird') },
    { name: 'reptile', value: t('reptile', 'Reptile'), label: t('reptile', 'Reptile') },
    { name: 'small_mammal', value: t('small_mammal', 'Small Mammal'), label: t('small_mammal', 'Small Mammal') },
    { name: 'other', value: t('other', 'Other'), label: t('other', 'Other') },
  ], [t]);

  const colorOptions = useMemo(() => [
    { name: 'dark_brown', value: t('dark_brown', 'Dark Brown'), label: t('dark_brown', 'Dark Brown') },
    { name: 'light_brown', value: t('light_brown', 'Light Brown'), label: t('light_brown', 'Light Brown') },
    { name: 'gray', value: t('gray', 'Gray'), label: t('gray', 'Gray') },
    { name: 'black', value: t('black', 'Black'), label: t('black', 'Black') },
    { name: 'white', value: t('white', 'White'), label: t('white', 'White') },
    { name: 'spotted', value: t('spotted', 'Spotted'), label: t('spotted', 'Spotted') },
    { name: 'orange', value: t('orange', 'Orange'), label: t('orange', 'Orange') },
    { name: 'blonde', value: t('blonde', 'Blonde'), label: t('blonde', 'Blonde') },
    { name: 'blend', value: t('blend', 'Blend'), label: t('blend', 'Blend') },
    { name: 'stripes', value: t('stripes', 'Stripes'), label: t('stripes', 'Stripes') },
    { name: 'other', value: t('other', 'Other'), label: t('other', 'Other') },
  ], [t]);

  const sexOptions = useMemo(() => [
    { name: 'male', value: t('male', 'Male'), label: t('male', 'Male') },
    { name: 'female', value: t('female', 'Female'), label: t('female', 'Female') },
    { name: 'unknown', value: t('unknown', 'Unknown'), label: t('unknown', 'Unknown') },
  ], [t]);

  const weightOptions = useMemo(() => [
    { name: 'weight_xsmall', value: t('weight_xsmall', '0-10 lbs'), label: t('weight_xsmall', '0-10 lbs') },
    { name: 'weight_small', value: t('weight_small', '10-25 lbs'), label: t('weight_small', '10-25 lbs') },
    { name: 'weight_medium', value: t('weight_medium', '25-50 lbs'), label: t('weight_medium', '25-50 lbs') },
    { name: 'weight_large', value: t('weight_large', '50-100 lbs'), label: t('weight_large', '50-100 lbs') },
    { name: 'weight_xlarge', value: t('weight_xlarge', '100+ lbs'), label: t('weight_xlarge', '100+ lbs') },
  ], [t]);
  
  // Form state
  const [formValues, setFormValues] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Image state for pet services
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [showImageCropper, setShowImageCropper] = useState(false);
  const [rawImageFile, setRawImageFile] = useState(null);
  const fileInputRef = useRef(null);
  
  // Cloudflare images hook
  const { handleUpload, handleDelete } = useCloudFlareImages();
  
  // Helper to extract filename from R2 URL
  const getImageNameFromUrl = (url) => {
    if (!url) return null;
    try {
      const urlObj = new URL(url);
      return urlObj.pathname.split('/').pop();
    } catch {
      return url.split('/').pop();
    }
  };

  // Mutations
  const [updateLostAnimal] = useMutation(UPDATE_LOST_ANIMAL);
  const [updateFoundAnimal] = useMutation(UPDATE_FOUND_ANIMAL);
  const [updatePSA] = useMutation(UPDATE_PSA);
  const [updatePetService] = useMutation(UPDATE_PET_SERVICE);

  useEffect(() => {
    if (postData && isOpen) {
      let colorMarkingsArray = [];
      if (Array.isArray(postData.colorMarkings)) {
        colorMarkingsArray = [...postData.colorMarkings];
      } else if (typeof postData.colorMarkings === 'string' && postData.colorMarkings) {
        colorMarkingsArray = postData.colorMarkings.split(',').map(s => s.trim()).filter(Boolean);
      }
      
      const initialValues = {
        title: postData.title || '',
        description: postData.description || '',
        // Animal-specific fields
        animalName: postData.animalName || '',
        animalType: postData.animalType || '',
        breed: postData.breed || '',
        colorMarkings: colorMarkingsArray,
        sex: postData.sex || '',
        estimatedWeight: postData.estimatedWeight || '',
        approachable: postData.approachable ?? null,
        // Location fields
        lastSeen: postData.lastSeen || '',
        foundNear: postData.foundNear || '',
        // Contact info
        contactName: postData.contactName || '',
      };
      setFormValues(initialValues);
      setError(null);
      setSuccess(false);
      
      // Initialize image preview for pet services
      if (postData.imageUrl) {
        setImagePreview(postData.imageUrl);
      } else {
        setImagePreview('');
      }
      setImageFile(null);
    }
  }, [postData, isOpen]);

  const handleClose = useCallback(() => {
    setFormValues({});
    setError(null);
    setSuccess(false);
    setImageFile(null);
    setImagePreview('');
    setShowImageCropper(false);
    setRawImageFile(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen && !isAuthenticated) {
      handleClose();
      navigate('/login');
    }
  }, [isOpen, isAuthenticated, navigate, handleClose]);

  const handleFieldChange = (name, value) => {
    setFormValues(prev => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
  };

  const handleColorToggle = (colorValue) => {
    setFormValues(prev => {
      const currentColors = Array.isArray(prev.colorMarkings) ? prev.colorMarkings : [];
      const isSelected = currentColors.includes(colorValue);
      
      return {
        ...prev,
        colorMarkings: isSelected 
          ? currentColors.filter(c => c !== colorValue)
          : [...currentColors, colorValue],
      };
    });
    setError(null);
  };

  const isColorSelected = useCallback((optionValue) => {
    if (!Array.isArray(formValues.colorMarkings)) return false;
    return formValues.colorMarkings.includes(optionValue);
  }, [formValues.colorMarkings]);

  const buildUpdateInput = () => {
    const normalizedType = postType?.toLowerCase?.() || postType;

    if (normalizedType === FEED_TYPES.LOST || normalizedType === 'lost' || normalizedType === 'lostanimal') {
      return {
        animalName: formValues.animalName,
        animalType: formValues.animalType,
        breed: formValues.breed,
        colorMarkings: formValues.colorMarkings || [],
        sex: formValues.sex,
        estimatedWeight: formValues.estimatedWeight,
        approachable: formValues.approachable,
        lastSeen: formValues.lastSeen,
        contactName: formValues.contactName,
      };
    }

    if (normalizedType === FEED_TYPES.FOUND || normalizedType === 'found' || normalizedType === 'foundanimal') {
      return {
        animalName: formValues.animalName,
        animalType: formValues.animalType,
        breed: formValues.breed,
        colorMarkings: formValues.colorMarkings || [],
        sex: formValues.sex,
        estimatedWeight: formValues.estimatedWeight,
        foundNear: formValues.foundNear,
        contactName: formValues.contactName,
      };
    }

    if (normalizedType === FEED_TYPES.PSA || normalizedType === 'psa') {
      return {
        title: formValues.title,
        description: formValues.description,
      };
    }

    if (normalizedType === FEED_TYPES.PET_SERVICE || normalizedType === 'petservice') {
      return {
        title: formValues.title,
        description: formValues.description,
      };
    }

    throw new Error(`Unknown post type: ${postType}`);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      const input = buildUpdateInput();
      const postId = postData._id || postData.id;
      const normalizedType = postType?.toLowerCase?.() || postType;

      // Handle image upload for pet services and PSAs (uses imageUrl)
      const useImageUrl = normalizedType === FEED_TYPES.PET_SERVICE || normalizedType === 'petservice' ||
                          normalizedType === FEED_TYPES.PSA || normalizedType === 'psa';
      if (useImageUrl) {
        const oldImageUrl = postData.imageUrl;
        
        if (imageFile) {
          const uploadedImageUrl = await handleUpload([imageFile]);
          input.imageUrl = uploadedImageUrl[0];
          
          if (oldImageUrl) {
            const oldImageName = getImageNameFromUrl(oldImageUrl);
            if (oldImageName) {
              try {
                await handleDelete(oldImageName);
              } catch (deleteErr) {
                console.warn('Failed to delete old image:', deleteErr);
              }
            }
          }
        } else if (imagePreview === '' && oldImageUrl) {
          input.imageUrl = null;
          
          const oldImageName = getImageNameFromUrl(oldImageUrl);
          if (oldImageName) {
            try {
              await handleDelete(oldImageName);
            } catch (deleteErr) {
              console.warn('Failed to delete old image:', deleteErr);
            }
          }
        } else if (imagePreview && !imageFile) {
          input.imageUrl = imagePreview;
        }
      }

      // Handle image upload for lost/found animals (uses animalImageUrl)
      const isAnimalType = normalizedType === FEED_TYPES.LOST || normalizedType === 'lost' || normalizedType === 'lostanimal' ||
                           normalizedType === FEED_TYPES.FOUND || normalizedType === 'found' || normalizedType === 'foundanimal';
      if (isAnimalType) {
        const oldImageUrl = postData.imageUrl;
        
        if (imageFile) {
          const uploadedImageUrl = await handleUpload([imageFile]);
          input.animalImageUrl = uploadedImageUrl[0];
          
          if (oldImageUrl) {
            const oldImageName = getImageNameFromUrl(oldImageUrl);
            if (oldImageName) {
              try {
                await handleDelete(oldImageName);
              } catch (deleteErr) {
                console.warn('Failed to delete old image:', deleteErr);
              }
            }
          }
        } else if (imagePreview === '' && oldImageUrl) {
          input.animalImageUrl = null;
          
          const oldImageName = getImageNameFromUrl(oldImageUrl);
          if (oldImageName) {
            try {
              await handleDelete(oldImageName);
            } catch (deleteErr) {
              console.warn('Failed to delete old image:', deleteErr);
            }
          }
        } else if (imagePreview && !imageFile) {
          input.animalImageUrl = imagePreview;
        }
      }

      if (normalizedType === FEED_TYPES.LOST || normalizedType === 'lost' || normalizedType === 'lostanimal') {
        await updateLostAnimal({ variables: { _id: postId, input } });
      } else if (normalizedType === FEED_TYPES.FOUND || normalizedType === 'found' || normalizedType === 'foundanimal') {
        await updateFoundAnimal({ variables: { _id: postId, input } });
      } else if (normalizedType === FEED_TYPES.PSA || normalizedType === 'psa') {
        await updatePSA({ variables: { _id: postId, input } });
      } else if (normalizedType === FEED_TYPES.PET_SERVICE || normalizedType === 'petservice') {
        await updatePetService({ variables: { _id: postId, input } });
      }

      setSuccess(true);
      dispatch(triggerFeedRefresh());

      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        }
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('Error updating post:', err);
      setError(err.message || t('error_updating_post', 'Error updating post'));
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isSaving) {
        handleClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isSaving, handleClose]);

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const normalizedType = postType?.toLowerCase?.() || postType;
  const isAnimalPost = normalizedType === FEED_TYPES.LOST || normalizedType === FEED_TYPES.FOUND ||
                       normalizedType === 'lost' || normalizedType === 'found' ||
                       normalizedType === 'lostanimal' || normalizedType === 'foundanimal';
  const isLostAnimal = normalizedType === FEED_TYPES.LOST || normalizedType === 'lost' || normalizedType === 'lostanimal';
  const isPetService = normalizedType === FEED_TYPES.PET_SERVICE || normalizedType === 'petservice';
  const isPSA = normalizedType === FEED_TYPES.PSA || normalizedType === 'psa';
  const supportsImage = isPetService || isAnimalPost || isPSA;

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setRawImageFile(file);
      setShowImageCropper(true);
    }
  };

  const handleCropComplete = (croppedFile) => {
    setImageFile(croppedFile);
    setImagePreview(URL.createObjectURL(croppedFile));
    setShowImageCropper(false);
    setRawImageFile(null);
  };

  const handleCropCancel = () => {
    setShowImageCropper(false);
    setRawImageFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <>
              {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm touch-none"
            onClick={!isSaving ? handleClose : undefined}
            onWheel={(e) => e.preventDefault()}
            onTouchMove={(e) => e.preventDefault()}
          />

          {/* Modal Container */}
          <div 
            className="fixed inset-0 z-[101] flex items-center justify-center p-0 md:p-4"
            onClick={!isSaving ? handleClose : undefined}
          >
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 50, scale: 0.95 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white flex flex-col w-full h-full
                         md:w-full md:max-w-lg md:h-auto md:max-h-[90vh] md:rounded-2xl md:shadow-2xl
                         overscroll-contain"
              role="dialog"
              aria-modal="true"
              aria-labelledby="edit-post-title"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100 flex-shrink-0">
                <h2 id="edit-post-title" className="text-lg font-semibold text-gray-900">
                  {t('edit_post', 'Edit Post')}
                </h2>
                <button
                  onClick={handleClose}
                  disabled={isSaving}
                  className="flex items-center justify-center w-10 h-10 -mr-2 rounded-full 
                             hover:bg-gray-100 transition-colors disabled:opacity-50"
                  aria-label={t('close', 'Close')}
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-4">
                {/* Success State */}
                {success && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-3 p-4 bg-green-50 text-green-700 rounded-xl border border-green-200"
                  >
                    <CheckCircle className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium">{t('post_updated_successfully', 'Post updated successfully!')}</span>
                  </motion.div>
                )}

                {/* Error State */}
                {error && (
                  <div className="p-4 bg-red-50 text-red-700 rounded-xl text-sm border border-red-200">
                    {error}
                  </div>
                )}

                {/* Animal Post Fields */}
                {isAnimalPost && !success && (
                  <>
                    {/* Animal Name */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        {t('animalName', 'Animal Name')}
                      </label>
                      <input
                        type="text"
                        value={formValues.animalName || ''}
                        onChange={(e) => handleFieldChange('animalName', e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 
                                   focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                        placeholder={t('enter_animal_name', 'Enter animal name')}
                      />
                    </div>

                    {/* Animal Type */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        {t('animal_type', 'Animal Type')}
                      </label>
                      <select
                        value={formValues.animalType || ''}
                        onChange={(e) => handleFieldChange('animalType', e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 
                                   focus:border-primary focus:ring-1 focus:ring-primary outline-none
                                   bg-white appearance-none cursor-pointer"
                      >
                        <option value="">{t('select_animal_type', 'Select animal type')}</option>
                        {animalTypeOptions.map((option) => (
                          <option key={option.name} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Breed */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        {t('breed', 'Breed')}
                      </label>
                      <input
                        type="text"
                        value={formValues.breed || ''}
                        onChange={(e) => handleFieldChange('breed', e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 
                                   focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                        placeholder={t('breedPlaceholder', 'Breed if known')}
                      />
                    </div>

                    {/* Color Markings */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        {t('colorMarkings', 'Color/Markings')}
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {colorOptions.map((option) => {
                          const isSelected = isColorSelected(option.value);
                          return (
                            <button
                              key={option.name}
                              type="button"
                              onClick={() => handleColorToggle(option.value)}
                              className={`px-3 py-2 text-xs font-medium rounded-lg border transition-colors
                                ${isSelected 
                                  ? 'bg-primary text-white border-primary' 
                                  : 'bg-white text-gray-700 border-gray-200 hover:border-primary hover:bg-gray-50'
                                }`}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Sex */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        {t('sex', 'Sex')}
                      </label>
                      <select
                        value={formValues.sex || ''}
                        onChange={(e) => handleFieldChange('sex', e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 
                                   focus:border-primary focus:ring-1 focus:ring-primary outline-none
                                   bg-white appearance-none cursor-pointer"
                      >
                        <option value="">{t('select_sex', 'Select sex')}</option>
                        {sexOptions.map((option) => (
                          <option key={option.name} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Estimated Weight */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        {t('estimatedWeight', 'Estimated Weight')}
                      </label>
                      <select
                        value={formValues.estimatedWeight || ''}
                        onChange={(e) => handleFieldChange('estimatedWeight', e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 
                                   focus:border-primary focus:ring-1 focus:ring-primary outline-none
                                   bg-white appearance-none cursor-pointer"
                      >
                        <option value="">{t('estimatedWeightPlaceholder', 'Select weight')}</option>
                        {weightOptions.map((option) => (
                          <option key={option.name} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Approachable */}
                    {isLostAnimal && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          {t('approachable', 'Is the animal approachable?')}
                        </label>
                        <div className="flex gap-4">
                          <label
                            className={`
                              flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl cursor-pointer
                              border-2 transition-all duration-200 font-medium
                              ${formValues.approachable === true
                                ? 'bg-green-100 border-green-500 text-green-700'
                                : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                              }
                            `}
                          >
                            <input
                              type="radio"
                              name="approachable"
                              checked={formValues.approachable === true}
                              onChange={() => handleFieldChange('approachable', true)}
                              className="sr-only"
                            />
                            {t('yes', 'Yes')}
                          </label>
                          <label
                            className={`
                              flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl cursor-pointer
                              border-2 transition-all duration-200 font-medium
                              ${formValues.approachable === false
                                ? 'bg-red-100 border-red-500 text-red-700'
                                : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                              }
                            `}
                          >
                            <input
                              type="radio"
                              name="approachable"
                              checked={formValues.approachable === false}
                              onChange={() => handleFieldChange('approachable', false)}
                              className="sr-only"
                            />
                            {t('no', 'No')}
                          </label>
                        </div>
                      </div>
                    )}

                    {/* Location */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        {isLostAnimal ? t('lastSeen', 'Last Seen Location') : t('foundNear', 'Found Near')}
                      </label>
                      <input
                        type="text"
                        value={isLostAnimal ? (formValues.lastSeen || '') : (formValues.foundNear || '')}
                        onChange={(e) => handleFieldChange(isLostAnimal ? 'lastSeen' : 'foundNear', e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 
                                   focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                        placeholder={t('location_description', 'Location description')}
                      />
                    </div>

                    {/* Contact Name */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        {t('contactName', 'Contact Name')}
                      </label>
                      <input
                        type="text"
                        value={formValues.contactName || ''}
                        onChange={(e) => handleFieldChange('contactName', e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 
                                   focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                        placeholder={t('your_name', 'Your name')}
                      />
                    </div>
                  </>
                )}

                {/* PSA / Pet Service Fields */}
                {!isAnimalPost && !success && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        {t('psaTitle', 'Title')}
                      </label>
                      <input
                        type="text"
                        value={formValues.title || ''}
                        onChange={(e) => handleFieldChange('title', e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 
                                   focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                        placeholder={t('enter_title', 'Enter title')}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        {t('psaDescription', 'Description')}
                      </label>
                      <textarea
                        value={formValues.description || ''}
                        onChange={(e) => handleFieldChange('description', e.target.value)}
                        rows={5}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 
                                   focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-none"
                        placeholder={t('enter_description', 'Enter description')}
                      />
                    </div>

                  </>
                )}

                {/* Image Upload */}
                {supportsImage && !success && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      {isAnimalPost
                        ? t('animal_image', 'Animal Photo (Optional)')
                        : isPSA
                          ? t('psa_image', 'Announcement Image (Optional)')
                          : t('service_image', 'Service Image (Optional)')}
                    </label>
                    
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt={isAnimalPost ? t('animal_preview', 'Animal preview') : isPSA ? t('psa_preview', 'Announcement preview') : t('service_preview', 'Service preview')}
                          className="w-full h-48 object-cover rounded-xl border border-gray-200"
                        />
                        <div className="absolute top-2 right-2 flex gap-2">
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="p-2 bg-white/90 rounded-full shadow-sm hover:bg-white transition-colors"
                            aria-label={t('change_image', 'Change image')}
                          >
                            <Camera className="w-4 h-4 text-gray-600" />
                          </button>
                          <button
                            type="button"
                            onClick={handleRemoveImage}
                            className="p-2 bg-white/90 rounded-full shadow-sm hover:bg-white transition-colors"
                            aria-label={t('remove_image', 'Remove image')}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full h-32 border-2 border-dashed border-gray-300 rounded-xl
                                   flex flex-col items-center justify-center gap-2
                                   hover:border-primary hover:bg-gray-50 transition-colors"
                      >
                        <Camera className="w-6 h-6 text-gray-400" />
                        <span className="text-sm text-gray-500">
                          {t('add_photo', 'Add a photo')}
                        </span>
                      </button>
                    )}
                    
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-4 py-4 border-t border-gray-100">
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={handleClose}
                    disabled={isSaving}
                    className="flex-1"
                  >
                    {success ? t('close', 'Close') : t('cancel', 'Cancel')}
                  </Button>
                  {!success && (
                    <Button
                      onClick={handleSave}
                      disabled={isSaving || success}
                      className="flex-1 bg-primary hover:bg-primary/90 text-white"
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('saving', 'Saving...')}
                        </>
                      ) : (
                        t('save', 'Save Changes')
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
        </AnimatePresence>,
        document.body
      )}
      
      {showImageCropper && rawImageFile && createPortal(
        <ImageCropper
          imageSrc={URL.createObjectURL(rawImageFile)}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          aspectRatio={4 / 3}
        />,
        document.body
      )}
    </>
  );
};

export default EditPostModal;
